import { useMutation } from "@tanstack/react-query";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import { type Entry, useCacheMutations } from "@/shared/cache";
import { useApi } from "@/shared/utils/request";
import { type AddPayload, addAction } from "@/v2/hooks/actions/add";
import { deleteAction } from "@/v2/hooks/actions/delete";
import { duplicateAction } from "@/v2/hooks/actions/duplicate";
import {
	type EditPayload,
	editAction,
	toUpdateBody,
} from "@/v2/hooks/actions/edit";
import {
	durationToUnitsSeconds,
	type EntryId,
	type EntryIdInput,
	formatEntryId,
} from "@/v2/hooks/actions/entryId";
import {
	type ExportResult,
	executeExport,
	exportAction,
} from "@/v2/hooks/actions/export";
import {
	createGroupedAction,
	type MutationAction,
	runMutationAction,
} from "@/v2/hooks/actions/mutationAction";
import { resolveFeedbackAction } from "@/v2/hooks/actions/resolveFeedback";
import {
	type SetStatusTarget,
	setStatusAction,
	setStatusManyAction,
} from "@/v2/hooks/actions/setStatus";
import {
	type ActionDeps,
	type ApiError,
	actingHeaders,
	apiCall,
} from "@/v2/hooks/actions/types";
import { groupEntriesByTimekeeper } from "@/v2/lib/selectionTimekeeperId";

class ExportAfterEditError extends Error {}

type ActionsContextType = {
	deleteEntries: (ids: EntryIdInput[], timekeeperId?: string | null) => void;
	editEntry: (entry: EditPayload) => void;
	editThenExport: (entry: EditPayload) => void;
	addEntry: (
		entry: AddPayload,
		onSuccess?: (newEntryId: EntryId | null) => void,
		toast?: MutationAction["toast"],
	) => void;
	duplicateEntries: (ids: EntryIdInput[], timekeeperId?: string | null) => void;
	exportEntries: (ids: EntryIdInput[], timekeeperId?: string | null) => void;
	deleteEntriesByOwner: (entries: Entry[]) => boolean;
	duplicateEntriesByOwner: (entries: Entry[]) => boolean;
	exportEntriesByOwner: (entries: Entry[]) => boolean;
	resolveFeedback: (entryId: EntryIdInput) => void;
	/**
	 * Move a single entry between cheap-path states (tracked, reviewed,
	 * deleted) via PATCH /v2/entries/{id}. Export transitions stay on the
	 * bulk POST /v2/entries/export action — use `exportEntries` for those.
	 */
	setStatus: (
		id: EntryIdInput,
		target: SetStatusTarget,
		onApiError?: (err: ApiError) => void,
		timekeeperId?: string | null,
	) => void;
	setStatusMany: (
		ids: EntryIdInput[],
		target: SetStatusTarget,
		onApiError?: (err: ApiError) => void,
		timekeeperId?: string | null,
	) => void;
	failedExports: Map<EntryId, string>;
	pendingEntryIds: Set<EntryId>;
};

const ActionsContext = createContext<ActionsContextType | undefined>(undefined);

const ENTRY_ACTION_MUTATION_KEY = ["entries", "actions"] as const;

export function ActionsProvider({ children }: { children: ReactNode }) {
	const { makeApiRequest } = useApi();
	const { optimisticUpdate, refresh } = useCacheMutations();
	const [failedExports, setFailedExports] = useState<Map<EntryId, string>>(
		new Map(),
	);
	const [pendingEntryIds, setPendingEntryIds] = useState<Set<EntryId>>(
		new Set(),
	);
	const pendingCountsRef = useRef(new Map<EntryId, number>());
	const { mutate: runActionMutation } = useMutation({
		mutationKey: ENTRY_ACTION_MUTATION_KEY,
		mutationFn: runMutationAction,
	});

	const updateEntries = useCallback(
		(updater: (prev: Entry[]) => Entry[]) =>
			optimisticUpdate("entries", updater),
		[optimisticUpdate],
	);
	const refreshEntries = useCallback(() => refresh("entries"), [refresh]);

	// Keep deps in a ref so actions always use the latest closures.
	const depsRef = useRef<ActionDeps>({
		makeApiRequest,
		updateEntries,
		refreshEntries,
	});
	depsRef.current = {
		makeApiRequest,
		updateEntries,
		refreshEntries,
	};

	// Stable deps proxy that always reads from the ref
	const stableDeps = useRef<ActionDeps>({
		makeApiRequest: (opts) => depsRef.current.makeApiRequest(opts),
		updateEntries: (updater) => depsRef.current.updateEntries(updater),
		refreshEntries: () => depsRef.current.refreshEntries(),
	}).current;

	const runAction = useCallback(
		(action: MutationAction, pendingIds: EntryIdInput[] = []) => {
			const pendingWireIds = pendingIds.map(formatEntryId);
			const applyPendingDelta = (delta: 1 | -1) => {
				if (pendingWireIds.length === 0) return;
				for (const id of pendingWireIds) {
					const current = pendingCountsRef.current.get(id) ?? 0;
					const next = current + delta;
					if (next <= 0) pendingCountsRef.current.delete(id);
					else pendingCountsRef.current.set(id, next);
				}
				setPendingEntryIds(new Set(pendingCountsRef.current.keys()));
			};
			if (pendingWireIds.length > 0) {
				applyPendingDelta(1);
			}
			runActionMutation(action, {
				onSettled: () => {
					applyPendingDelta(-1);
				},
			});
		},
		[runActionMutation],
	);

	// --- Export completion handler ---

	const handleExportComplete = useCallback((result: ExportResult) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			// Clear successes
			for (const id of result.exportedIds) next.delete(id);
			// Add failures
			for (const { id, error } of result.failedEntries) next.set(id, error);
			return next;
		});
	}, []);

	const recordExportFailure = useCallback((id: EntryId, error: string) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			next.set(id, error);
			return next;
		});
	}, []);

	// --- Clear failed export when entry is edited or deleted ---

	const clearFailedExports = useCallback((ids: EntryIdInput[]) => {
		const wireIds = ids.map(formatEntryId);
		setFailedExports((prev) => {
			if (!wireIds.some((id) => prev.has(id))) return prev;
			const next = new Map(prev);
			for (const id of wireIds) next.delete(id);
			return next;
		});
	}, []);

	// --- Dispatch helpers ---

	const deleteEntries = useCallback(
		(ids: EntryIdInput[], timekeeperId?: string | null) => {
			clearFailedExports(ids);
			runAction(deleteAction(ids, stableDeps, timekeeperId), ids);
		},
		[runAction, stableDeps, clearFailedExports],
	);

	const editEntry = useCallback(
		(entry: EditPayload) => {
			clearFailedExports([entry.id]);
			runAction(editAction(entry, stableDeps), [entry.id]);
		},
		[runAction, stableDeps, clearFailedExports],
	);

	const editThenExport = useCallback(
		(entry: EditPayload) => {
			const id = entry.id;
			const entryId = formatEntryId(id);
			clearFailedExports([id]);
			const undoOptimistic = stableDeps.updateEntries((prev) =>
				prev.map((e) => {
					if (e.id !== entryId) return e;
					return {
						...e,
						description: entry.description,
						units: entry.duration,
						unitsSeconds: durationToUnitsSeconds(entry.duration),
						matter: e.matter
							? { ...e.matter, id: entry.matter_id || null }
							: entry.matter_id
								? {
										id: entry.matter_id,
										code: null,
										description: entry.matter || null,
										client: null,
									}
								: null,
						codes: {
							...e.codes,
							taskCode: entry.task || null,
							activityCode: entry.activity || null,
						},
					};
				}),
			);
			const actionId = crypto.randomUUID();
			let editSucceeded = false;
			runAction(
				{
					id: actionId,
					execute: async () => {
						await apiCall(stableDeps, {
							endpoint: `/v2/entries/${entryId}`,
							method: "PATCH",
							body: toUpdateBody(entry),
							headers: actingHeaders(entry.timekeeperId),
						});
						editSucceeded = true;
						try {
							await executeExport(
								actionId,
								[id],
								stableDeps,
								handleExportComplete,
								recordExportFailure,
								entry.timekeeperId,
							);
						} catch (err) {
							throw new ExportAfterEditError(
								err instanceof Error ? err.message : "Export failed",
							);
						}
					},
					rollback: () => {
						if (!editSucceeded) undoOptimistic();
					},
					toast: {
						loading: "Saving and exporting…",
						success: "Entry saved and exported",
						error: (err) =>
							err instanceof ExportAfterEditError
								? "Entry saved but export failed"
								: "Failed to save entry",
					},
					onSuccess: () => stableDeps.refreshEntries(),
					onError: () => stableDeps.refreshEntries(),
				},
				[id],
			);
		},
		[
			runAction,
			stableDeps,
			clearFailedExports,
			handleExportComplete,
			recordExportFailure,
		],
	);

	const addEntry = useCallback(
		(
			entry: AddPayload,
			onSuccess?: (newEntryId: EntryId | null) => void,
			toast?: MutationAction["toast"],
		) => runAction(addAction(entry, stableDeps, onSuccess, toast)),
		[runAction, stableDeps],
	);

	const duplicateEntries = useCallback(
		(ids: EntryIdInput[], timekeeperId?: string | null) =>
			runAction(duplicateAction(ids, stableDeps, timekeeperId), ids),
		[runAction, stableDeps],
	);

	const exportEntries = useCallback(
		(ids: EntryIdInput[], timekeeperId?: string | null) =>
			runAction(
				exportAction(
					ids,
					stableDeps,
					handleExportComplete,
					recordExportFailure,
					timekeeperId,
				),
				ids,
			),
		[runAction, stableDeps, handleExportComplete, recordExportFailure],
	);

	const runGroupedEntries = useCallback(
		(
			entries: Entry[],
			build: (ids: EntryIdInput[], ownerId: string) => MutationAction,
			toastCopy: MutationAction["toast"],
		) => {
			const groups = groupEntriesByTimekeeper(entries);
			if (groups === false) return false;
			const ids = entries.map((entry) => entry.id);
			runAction(
				createGroupedAction(
					groups.map((group) =>
						build(
							group.entries.map((entry) => entry.id),
							group.timekeeperId,
						),
					),
					toastCopy,
				),
				ids,
			);
			return true;
		},
		[runAction],
	);

	const deleteEntriesByOwner = useCallback(
		(entries: Entry[]) => {
			clearFailedExports(entries.map((entry) => entry.id));
			return runGroupedEntries(
				entries,
				(ids, ownerId) => deleteAction(ids, stableDeps, ownerId),
				{
					loading: "Deleting entries…",
					success: "Entries deleted",
					error: "Some timekeeper deletes failed",
				},
			);
		},
		[clearFailedExports, runGroupedEntries, stableDeps],
	);

	const duplicateEntriesByOwner = useCallback(
		(entries: Entry[]) =>
			runGroupedEntries(
				entries,
				(ids, ownerId) => duplicateAction(ids, stableDeps, ownerId),
				{
					loading: "Duplicating entries…",
					success: "Entries duplicated",
					error: "Some timekeeper duplicates failed",
				},
			),
		[runGroupedEntries, stableDeps],
	);

	const exportEntriesByOwner = useCallback(
		(entries: Entry[]) =>
			runGroupedEntries(
				entries,
				(ids, ownerId) =>
					exportAction(
						ids,
						stableDeps,
						handleExportComplete,
						recordExportFailure,
						ownerId,
					),
				{
					loading: "Exporting entries…",
					success: "Entries exported",
					error: "Some timekeeper exports failed",
				},
			),
		[handleExportComplete, recordExportFailure, runGroupedEntries, stableDeps],
	);

	const resolveFeedback = useCallback(
		(entryId: EntryIdInput) =>
			runAction(resolveFeedbackAction(entryId, stableDeps), [entryId]),
		[runAction, stableDeps],
	);

	const setStatus = useCallback(
		(
			id: EntryIdInput,
			target: SetStatusTarget,
			onApiError?: (err: ApiError) => void,
			timekeeperId?: string | null,
		) => {
			clearFailedExports([id]);
			runAction(
				setStatusAction(id, target, stableDeps, onApiError, timekeeperId),
				[id],
			);
		},
		[runAction, stableDeps, clearFailedExports],
	);

	const setStatusMany = useCallback(
		(
			ids: EntryIdInput[],
			target: SetStatusTarget,
			onApiError?: (err: ApiError) => void,
			timekeeperId?: string | null,
		) => {
			if (ids.length === 0) return;
			clearFailedExports(ids);
			if (ids.length === 1) {
				runAction(
					setStatusAction(ids[0], target, stableDeps, onApiError, timekeeperId),
					[ids[0]],
				);
				return;
			}
			runAction(
				setStatusManyAction(ids, target, stableDeps, onApiError, timekeeperId),
				ids,
			);
		},
		[runAction, stableDeps, clearFailedExports],
	);

	return (
		<ActionsContext.Provider
			value={{
				deleteEntries,
				editEntry,
				editThenExport,
				addEntry,
				duplicateEntries,
				exportEntries,
				deleteEntriesByOwner,
				duplicateEntriesByOwner,
				exportEntriesByOwner,
				resolveFeedback,
				setStatus,
				setStatusMany,
				failedExports,
				pendingEntryIds,
			}}
		>
			{children}
		</ActionsContext.Provider>
	);
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

export function useActions() {
	const ctx = useContext(ActionsContext);
	if (!ctx) throw new Error("useActions must be used within ActionsProvider");
	return ctx;
}
